<template>
  <v-app>
    <div class="maintenanceList">
      <v-tabs
        v-model="tab"
        grow
        centered
        icons-and-text
        @change="onTabChange"
      >
        <v-tab href="#tab-1">
          <p>New Requests</p>
          <v-icon>mdi-new-box</v-icon>
        </v-tab>

        <v-tab href="#tab-2">
          <p>Assigned Requests</p>
          <v-icon>mdi-progress-clock</v-icon>
        </v-tab>

        <v-tab href="#tab-3">
          <p>Closed Requests</p>
          <v-icon>mdi-chart-box-multiple-outline</v-icon>
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item value="tab-1">
          <v-card>
            <v-card-title>
              New Requests
              <v-spacer></v-spacer>
              <div class="d-flex justify-content-end align-items-center gap-3" style="gap: 10px;">

                  <v-text-field
                    v-model="searchNew"
                    class="mr-3"
                    append-icon="mdi-magnify"
                    :label="$t('GeneralTranslations.Search')"
                    single-line
                    hide-details
                    style="min-width: 300px"
                  ></v-text-field>

                  <v-dialog v-model="dialog" max-width="700px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn color="primary" v-bind="attrs" v-on="on">New +</v-btn>
                    </template>

                    <v-card>
                      <validation-observer ref="form" v-slot="{ invalid }">
                        <v-form ref="createMaintenanceRequestForm" lazy-validation :disabled="isMediaUploading" @submit.prevent="submit">
                          <v-card-title>
                            <span class="headline">Create Maintenance Request</span>
                          </v-card-title>
                          <v-card-text>
                            <v-container>
                              <v-row align="center">
                                <v-col cols="12">
                                  <validation-provider v-slot="{ errors }" name="RequestType" rules="required">
                                    <v-autocomplete
                                      v-model="reqType"
                                      :items="maintenanceTypes"
                                      density="compact"
                                      variant="solo"
                                      item-text="name"
                                      return-object
                                      label="Request Type*"
                                      :error-messages="errors"
                                      required
                                    ></v-autocomplete>
                                  </validation-provider>
                                </v-col>

                                <v-col cols="6">
                                  <v-textarea
                                    v-model="newMaintenanceRequest.description"
                                    label="Request Description"
                                    rows="2"
                                    hint="Provide more information on the specific location or details related to the issue."
                                  ></v-textarea>
                                </v-col>

                                <v-col cols="6">
                                  <v-textarea
                                    v-model="newMaintenanceRequest.comment"
                                    label="Comment"
                                    rows="2"
                                  ></v-textarea>
                                </v-col>

                                <v-col cols="12" class="pt-0 pb-0">
                                  <small>Selecting a resident is optional but useful if you want to link this ticket to resident and their room.</small>
                                </v-col>

                                <v-col cols="6">
                                  <v-autocomplete
                                    v-model="newMaintRes"
                                    :items="residentsList"
                                    density="compact"
                                    variant="solo"
                                    item-text="name"
                                    return-object
                                    label="Resident"
                                    clearable
                                    @click:clear="clearRes()"
                                  ></v-autocomplete>
                                </v-col>

                                <v-col cols="6">
                                  <v-text-field
                                    v-model="newMaintenanceRequest.roomName"
                                    label="Room"
                                    :disabled="newMaintRes != null"
                                    required
                                  ></v-text-field>
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-col cols="6" class="pb-0">
                                  <v-autocomplete
                                    :items="eventLocations"
                                    item-text="name"
                                    item-value="id"
                                    v-model.trim="newMaintenanceRequest.locationId"
                                    :label="`${$t('NewCalendarItem.Location')}`"
                                    :search-input.sync="newLocationName"
                                    ref="location"
                                    class="maintenance-location"
                                  >
                                    <template v-if="!eventLocations.find(x => x.name === newLocationName)" #append-item="">
                                      <v-btn
                                        class="ml-2 mt-2"
                                        color="primary"
                                        width="96%"
                                        @click="addNewLocation()"
                                        :disabled="isDisable || !newLocationName"
                                      >{{ $t('NewCalendarItem.AddNewLocation') }}</v-btn>
                                    </template>
                                  </v-autocomplete>
                                </v-col>
                                <v-col cols="6" class="pb-0">
                                  <v-autocomplete
                                    class="select-wings"
                                    v-model="newMaintenanceRequest.wingId"
                                    :items="wings"
                                    :label="$t('NewCalendarItem.Wing')"
                                    ref="wings"
                                    item-text="name"
                                    item-value="id"
                                    @click:append-outer="openAllWings"
                                    :search-input.sync="newWingName"
                                  >
                                    <template v-if="!wings.find(x => x.name === newWingName) && isStaffCanCreateWings && !isFacilityHasCareAPI" #append-item="">
                                      <v-btn
                                        class="ml-2 mt-2"
                                        color="primary"
                                        width="94%"
                                        @click="addNewWing()"
                                        :disabled="isDisableWing || !newWingName"
                                      >{{ $t('AdmitResidentsDialog.AddNewWing') }}</v-btn>
                                    </template>
                                  </v-autocomplete>
                                </v-col>
                              </v-row>

                              <v-row>
                                <v-col cols="12" class="pb-0">
                                  <validation-provider v-slot="{ errors }" name="Priority" rules="required">
                                    <v-select
                                      :label="$t('MaintenanceSchedule.Priority') + ' *'"
                                      v-model="newMaintenanceRequest.priority"
                                      :items="maintenancePriority"
                                      :error-messages="errors"
                                      required
                                    ></v-select>
                                  </validation-provider>
                                </v-col>

                                <v-col cols="3">
                                  <validation-provider rules="required" v-slot="{ errors }">
                                    <p class="mb-2">{{ $t("MaintenanceSchedule.Risk") }} *</p>

                                    <v-radio-group v-model="newMaintenanceRequest.risk" :error-messages="errors">
                                      <v-radio :label="$t('MaintenanceSchedule.N/A')" value="n/a"></v-radio>
                                      <v-radio :label="$t('MaintenanceSchedule.Yes')" value="yes"></v-radio>
                                      <v-radio :label="$t('MaintenanceSchedule.No')" value="no"></v-radio>
                                    </v-radio-group>
                                  </validation-provider>
                                </v-col>

                                <v-col cols="6">
                                  <v-textarea
                                    v-model="newMaintenanceRequest.riskDetails"
                                    :label="$t('MaintenanceSchedule.RiskDetails')"
                                    rows="4"
                                  ></v-textarea>
                                </v-col>

                                <v-col cols="3">
                                  <validation-provider rules="required" v-slot="{ errors }">
                                    <p class="mb-2">{{ $t("MaintenanceSchedule.Type") }} *</p>

                                    <v-radio-group v-model="newMaintenanceRequest.maintenanceType" :error-messages="errors">
                                      <v-radio :value="MaintenanceTypes.Repair" :label="$t('MaintenanceSchedule.Repair')"></v-radio>
                                      <v-radio :value="MaintenanceTypes.Prevention" :label="$t('MaintenanceSchedule.Prevention')"></v-radio>
                                    </v-radio-group>
                                  </validation-provider>
                                </v-col>

                                <v-col cols="12">
                                   <v-row>
                                    <v-col cols="auto" class="d-flex justify-start">
                                      <h5 class="mb-0 mt-2">{{ $t('MaintenanceSchedule.Checklist') }}</h5>
                                    </v-col>
                                     <v-col cols="auto" class="d-flex justify-start">
                                      <v-btn small @click="addItemForChecklist" class="primary">{{ $t('MaintenanceSchedule.AddItem') }}</v-btn>
                                    </v-col>
                                    <v-spacer />
                                     <v-col class="d-flex justify-end">
                                      <v-autocomplete label="Assign job request" 
                                        :loading="isMaintenanceTechniciansLoading" 
                                        style="width: 250px" :items="maintenanceTechnicians" 
                                        @change="onStaffAssign"
                                        return-object
                                        item-text="name"
                                        tem-value="id">
                                      </v-autocomplete>
                                    </v-col>
                                    </v-row>
                                    <v-list>
                                      <v-list-item
                                        class="p-0"
                                        v-for="(item, index) in newMaintenanceRequest.checklist"
                                        :key="index"
                                      >
                                        <v-list-item-content>
                                          <v-text-field
                                            v-model="item.label"
                                            hide-details
                                          ></v-text-field>
                                        </v-list-item-content>

                                        <v-list-item-action style="flex-direction: inherit">
                                          <v-icon color="red" class="ml-3" @click="removeItemFromChecklist(index)">mdi-delete</v-icon>
                                        </v-list-item-action>
                                      </v-list-item>
                                    </v-list>
                                </v-col>

                                <v-col cols="12" class="email-request-to-vendor pb-0">
                                  <v-checkbox
                                    v-model="newMaintenanceRequest.isSendEmailToVendor"
                                    :label="$t('MaintenanceSchedule.EmailRequestToVendor')"
                                  ></v-checkbox>
                                </v-col>

                                <v-col cols="12" class="pb-0">
                                  <v-checkbox
                                    v-model="newMaintenanceRequest.isHazardous"
                                    :label="$t('MaintenanceSchedule.Hazardous')"
                                  ></v-checkbox>

                                  <v-textarea
                                    v-if="newMaintenanceRequest.isHazardous"
                                    v-model="newMaintenanceRequest.hazardousDetails"
                                    :label="$t('MaintenanceSchedule.Hazard')"
                                    rows="2"
                                  ></v-textarea>
                                </v-col>

                                <v-col v-if="canUploadMaintenenceMedia" cols="12" class="d-flex align-center">
                                  <MaintenanceUploadMedia ref="maintenanceUploadMediaRef" :key="uploadKey" :facilityId="facility.id" :requestId="null" @onMaintenanceMediaBeforeUpload="onMaintenanceMediaBeforeUpload" @onMaintenanceMediaUploaded="onMaintenanceMediaUploaded"></MaintenanceUploadMedia>
                                </v-col>

                                <v-col cols="12">
                                  <small>Additional location based information.</small>
                                </v-col>
                           
                              </v-row>

                            </v-container>
                            <small class="pl-3">{{ $t('GeneralTranslations.StarIndicatesRequiredField') }}</small>
                          </v-card-text>

                          <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn outlined color="primary" @click="close">{{ $t('GeneralTranslations.Cancel') }}</v-btn>

                            <v-btn type="submit" color="primary" :disabled="invalid">
                              {{ $t("GeneralTranslations.Create") }}
                            </v-btn>
                          </v-card-actions>
                        </v-form>
                      </validation-observer>
                    </v-card>
                  </v-dialog>

                  <export-excel
                    :data="mergeAllRequests"
                    :fields="allRequestsJsonFields"
                    class="btn btn-default"
                    style="padding: 0; border: 0;"
                    name="NewMaintenanceRequests.csv"
                    type="csv"
                  >
                    <v-btn class="primary white--text">{{ $t('GeneralTranslations.Export') }}</v-btn>
                  </export-excel>

                  <v-btn color="primary" @click.native="openQRCode">
                    Open QR Code
                  </v-btn>
              </div>
            </v-card-title>

            <!-- Mass Action Buttons for New Requests -->
            <v-alert 
              v-if="selectedRequestsNew.length > 0 && canPerformMassActions"
              dense
              color="grey lighten-4"
              class="ma-4"
            >
              <div class="d-flex align-items-center flex-wrap" style="gap: 10px;">
                <span class="mr-3">
                  <strong>{{ selectedRequestsNew.length }}</strong> {{ selectedRequestsNew.length === 1 ? 'request' : 'requests' }} selected
                </span>
                <v-btn color="primary" small @click="openCancelDialog">
                  Cancel Request
                </v-btn>
                <v-btn color="primary" small @click="openChangeDueDateDialog">
                  Change due date
                </v-btn>
                <v-btn color="primary" small @click="openAssignToDialog">
                  Assign to
                </v-btn>
                <v-btn color="primary" small @click="openPriorityDialog">
                  Assign Priority
                </v-btn>
              </div>
            </v-alert>

            <v-data-table
              ref="newRequestsTable"
              :loading="loading"
              :no-data-text="$t('MaintenanceList.NoDataText')"
              :headers="headers"
              :items="maintenanceRequestsNew"
              :search="searchNew"
              :items-per-page="10"
              :footer-props="{
                showFirstLastPage: true,
                itemsPerPageOptions: [10, 25, 50, 100, -1]
              }"
              :sort-by.sync="sortByNew"
              :sort-desc.sync="sortDescNew"
              item-key="id"
              show-select
              v-model="selectedRequestsNewObjects"
              @click:row="handleClickMaintenanceRequest"
              @pagination="onNewRequestsPagination"
              must-sort
            >
              <template v-slot:item.friendlyText="{ item }">
                <span v-if="item.friendlyText">{{
                  $t(
                    `Maintenance.CategoryText.${convertToTranslationKey(
                      item.friendlyText
                    )}`
                  )
                }}</span>
                <span v-if="item.name">{{ item.name }}</span>
              </template>

              <template v-slot:item.category="{ item }">
                <span v-if="checkExistCategory(item.category)">{{
                  $t(
                    `Maintenance.Category.${convertToTranslationKey(
                      item.category
                    )}`
                  )
                }}</span>
                <span v-else>{{ item.category }}</span>
              </template>

              <template v-slot:item.icon="{ item }">
                <v-icon
                  large
                  color="primary"
                  >{{ getIcon(item.icon) }}</v-icon
                >
              </template>

              <template v-slot:item.roomName="{ item }">
                <span>{{ (item.roomName ? item.roomName : '-') + ' / ' + (item.location ? item.location : '-')}}</span>
              </template>

              <template v-slot:item.requestAge="{ item }">
                <span :class="item.requestAgeColor">
                  <b>{{ item?.hoursAgo }}</b>
                </span>
              </template>

              <template v-slot:item.estimatedCompletionDate="{ item }">
                <MaintenanceDueDateDialog :request="item" @updated="fetchData" />
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>

        <v-tab-item value="tab-2">
          <v-card>
            <v-card-title>
              Assigned Requests
              <v-spacer></v-spacer>
              <v-text-field
                v-model="searchAssigned"
                append-icon="mdi-magnify"
                :label="$t('GeneralTranslations.Search')"
                single-line
                hide-details
              ></v-text-field>
            </v-card-title>

            <!-- Mass Action Buttons for Assigned Requests -->
            <v-alert 
              v-if="selectedRequestsInProgress.length > 0 && canPerformMassActions"
              dense
              color="grey lighten-4"
              class="ma-4"
            >
              <div class="d-flex align-items-center flex-wrap" style="gap: 10px;">
                <span class="mr-3">
                  <strong>{{ selectedRequestsInProgress.length }}</strong> {{ selectedRequestsInProgress.length === 1 ? 'request' : 'requests' }} selected
                </span>
                <v-btn color="primary" small @click="openCancelDialog">
                  Cancel Request
                </v-btn>
                <v-btn color="primary" small @click="openChangeDueDateDialog">
                  Change due date
                </v-btn>
                <v-btn color="primary" small @click="openAssignToDialog">
                  Assign to
                </v-btn>
                <v-btn color="primary" small @click="openPriorityDialog">
                  Priority
                </v-btn>
              </div>
            </v-alert>

            <v-data-table
              ref="inProgressRequestsTable"
              :loading="loading"
              :no-data-text="$t('MaintenanceList.NoDataText')"
              :headers="headersInProgress"
              :items="maintenanceRequestsInProgress"
              :search="searchAssigned"
              :items-per-page="10"
              :footer-props="{
                showFirstLastPage: true,
                itemsPerPageOptions: [10, 25, 50, 100, -1]
              }"
              :sort-by.sync="sortByInProgress"
              :sort-desc.sync="sortDescInProgress"
              item-key="id"
              show-select
              v-model="selectedRequestsInProgressObjects"
              @click:row="handleClickMaintenanceRequest"
              @pagination="onInProgressRequestsPagination"
              must-sort
            >
              <template v-slot:item.friendlyText="{ item }">
                <span v-if="item.friendlyText">{{
                  $t(
                    `Maintenance.CategoryText.${convertToTranslationKey(
                      item.friendlyText
                    )}`
                  )
                }}</span>
                <span v-if="item.name">{{ item.name }}</span>
              </template>

              <template v-slot:item.category="{ item }">
                <span v-if="checkExistCategory(item.category)">{{
                  $t(
                    `Maintenance.Category.${convertToTranslationKey(
                      item.category
                    )}`
                  )
                }}</span>
                <span v-else>{{ item.category }}</span>
              </template>

              <template v-slot:item.status="{ item }">
                <span>{{ getStatus(item.status) }}</span>
              </template>

              <template v-slot:item.icon="{ item }">
                <v-icon large color="primary">
                  {{ getIcon(item.icon) }}
                </v-icon>
              </template>
              
              <template v-slot:item.roomName="{ item }">
                <span>{{ item.roomName + " " + item.location }}</span
                >
              </template>

              <template v-slot:item.requestAge="{ item }">
                <span :class="item.requestAgeColor">
                  <b>{{ item?.hoursAgo }}</b>
                </span>
              </template>

              <template v-slot:item.priority="{ item }">
                <span :class="item.priorityColor">
                  <b>{{ item?.priority }}</b>
                </span>
              </template>

              <template v-slot:item.estimatedCompletionDate="{ item }">
                <MaintenanceDueDateDialog :request="item" @updated="fetchData" />
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>

        <v-tab-item value="tab-3">
          <v-card>
            <v-card-title>
              Closed Requests
              <v-spacer></v-spacer>
            </v-card-title>

            <v-flex
              lg12
              md12
              xs12
            >
              <v-row class="mh-fix">
                <v-col cols="2">
                  <v-menu
                    v-model="picker.start"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="formattedStartDate"
                        :label="$t('GeneralTranslations.StartDate')"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      :locale="$i18n.locale"
                      v-model="startDate"
                      @input="picker.start = false"
                    >
                    </v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="2">
                  <v-menu
                    v-model="picker.end"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="formattedEndDate"
                        :label="$t('GeneralTranslations.EndDate')"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      >
                      </v-text-field>
                    </template>
                    <v-date-picker
                      :locale="$i18n.locale"
                      v-model="endDate"
                      @input="picker.end = false"
                    >
                    </v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="1">
                  <v-btn
                    class="primary"
                    @click="loadClosedRequests"
                    :disabled="loading"
                    >{{ $t('GeneralTranslations.Update') }}</v-btn
                  >
                </v-col>
                <v-col cols="1">
                  <export-excel
                    :data="closedMaintenanceRequests"
                    :fields="jsonFields"
                    name="ClosedMaintenanceRequests.csv"
                    type="csv"
                  >
                    <v-btn class="primary">{{
                      $t('GeneralTranslations.Export')
                    }}</v-btn>
                  </export-excel>
                </v-col>
              </v-row>
            </v-flex>

            <v-flex
              lg12
              md12
              xs12
            >
              <v-row class="">
                <v-card class="m-2 ml-4 mt-4 mb-4">
                  <v-card-title>
                    Requests
                    <v-spacer></v-spacer>
                    <v-icon>mdi-spanner</v-icon>
                  </v-card-title>

                  <v-card-text class="py-0 pb-10">
                    <v-row
                      align="center"
                      no-gutters
                    >
                      <v-col
                        align="center"
                        cols="12"
                      >
                        <v-col cols="12">
                          <span class="text-h2">{{closedMaintenanceRequests?.length ?? 0 }}</span>
                        </v-col>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>

                <v-card class="m-2 mt-4 mb-4">
                  <v-card-title>
                    Priority
                    <v-spacer></v-spacer>
                    <v-icon>mdi-spanner</v-icon>
                  </v-card-title>

                  <v-card-text class="py-0">
                    <v-row
                      align="center"
                      no-gutters
                    >
                      <v-col
                        align="center"
                        cols="12"
                      >
                        <pie-chart
                          :data="closedByPriority"
                          height="160px"
                          :donut="true"
                          :colors="['#ff8080', '#CDF69A', '#FFBD31', '#ff8080']"
                        ></pie-chart
                      ></v-col>
                    </v-row>
                  </v-card-text>
                </v-card>

                <v-card class="m-2 mt-4 mb-4">
                  <v-card-title>
                    Category
                    <v-spacer></v-spacer>
                    <v-icon>mdi-spanner</v-icon>
                  </v-card-title>

                  <v-card-text class="py-0">
                    <v-row
                      align="center"
                      no-gutters
                    >
                      <v-col
                        align="center"
                        cols="12"
                      >
                        <column-chart
                          :data="closedByCategory"
                          :colors="['1948b3']"
                          height="180px"
                        ></column-chart
                      ></v-col>
                    </v-row>
                  </v-card-text>
                </v-card>

                <v-card class="ml-6 mr-5 mt-4 mb-4">
                  <v-card-title>
                    Avg. Time to Close
                  </v-card-title>

                  <v-card-text class="py-0 pb-5">
                    <v-row
                      align="center"
                      no-gutters
                    >
                      <v-col
                        align="center"
                        cols="12"
                      >
                        <v-col cols="12">
                          <span class="text-h3">
                            <span v-if="closedMaintenanceRequests.length">{{Math.round(closedMaintenanceRequests.reduce((a, b) => a + b.openDuration, 0) / closedMaintenanceRequests.length)}}</span>
                            <span v-else>0</span>
                            days
                          </span>
                        </v-col>
                      </v-col>
                    </v-row>
                    
                  </v-card-text>
                </v-card>
              </v-row>
            </v-flex>

            <v-flex
              lg12
              md12
              xs12
            >
              <v-data-table
                :loading="loading"
                :no-data-text="$t('MaintenanceList.NoDataText')"
                :headers="headersClosed"
                :items="closedMaintenanceRequests"
                :items-per-page="10"
                :footer-props="{
                  showFirstLastPage: true,
                  itemsPerPageOptions: [10, 25, 50, 100, -1]
                }"
                :sort-by="['dateTime']"
                :sort-desc="[true]"
                item-key="id"
                @click:row="handleClickMaintenanceRequest"
              >
                <template v-slot:item.friendlyText="{ item }">
                  <span v-if="item.friendlyText">{{
                    $t(
                      `Maintenance.CategoryText.${convertToTranslationKey(
                        item.friendlyText
                      )}`
                    )
                  }}</span>
                  <span v-if="item.name">{{ item.name }}</span>
                </template>

                <template v-slot:item.category="{ item }">
                  <span v-if="checkExistCategory(item.category)">{{
                    $t(
                      `Maintenance.Category.${convertToTranslationKey(
                        item.category
                      )}`
                    )
                  }}</span>
                  <span v-else>{{ item.category }}</span>
                </template>

                <template v-slot:item.status="{ item }">
                  <span>{{ getStatus(item.status) }}</span>
                </template>

                <template v-slot:item.icon="{ item }">
                  <v-icon
                    large
                    color="primary"
                    >{{ getIcon(item.icon) }}</v-icon
                  >
                </template>

                <template v-slot:item.requestAge="{ item }">
                  <span :class="item.requestAgeColor">
                    <b>{{ item?.hoursAgo }}</b>
                  </span>
                </template>

                <template v-slot:item.priority="{ item }">
                  <span :class="item.priorityColor">
                    <b>{{ item?.priority }}</b>
                  </span>
                </template>

                <template v-slot:item.estimatedCompletionDate="{ item }">
                  <span>{{ item?.estimatedCompletionDate ? formatDueDate(item.estimatedCompletionDate) : '-' }}</span>
                </template>
              </v-data-table>
            </v-flex>
          </v-card>
        </v-tab-item>
      </v-tabs-items>

      <MaintenanceQRCodeDlg ref="maintenanceQRCodeDlg"/>
      
      <!-- Mass Action Dialogs -->
      <MassActionDialog ref="cancelDialog" action-type="cancel" @completed="onMassActionCompleted" />
      <MassActionDialog ref="dueDateDialog" action-type="dueDate" @completed="onMassActionCompleted" />
      <MassActionDialog ref="assignDialog" action-type="assign" @completed="onMassActionCompleted" />
      <MassActionDialog ref="priorityDialog" action-type="priority" @completed="onMassActionCompleted" />
    </div>
  </v-app>
</template>

<script>
import { toTranslationKey } from '@/utils/stringFormat';
import { maintenanceCategoriesKeys } from '@/consts/MaintenanceCategoriesKeys';
import { MaintenanceMixin } from '@/mixins/Maintenance';
import { EventLocationsMixin } from "@/mixins/EventLocations";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import MaintenanceQRCodeDlg from "@/views/maintenance/maintenanceQRCodeDlg/maintenanceQRCodeDlg.vue";
import MaintenanceDueDateDialog from "@/views/maintenance/maintenanceDueDateDialog/MaintenanceDueDateDialog.vue";
import MassActionDialog from "@/components/maintenance/MassActionDialog.vue";
import router from '@/router';
import moment from 'moment';
import {MaintenanceRequestStatus} from "@/enums/MaintenanceRequestStatus";
import {ResidentStatus} from "@/enums/ResidentStatus";
import {MaintenancePriority} from "@/enums/MaintenancePriority";
import { MaintenanceTypes } from '@/enums/MaintenanceTypes';
import { hasAccess } from "@/utils/accessControl/accessControl";
import { AccessMatrixKeys } from "@/utils/accessControl/accessMatrixKeysEnum";
import { MaintenanceChecklist } from '@/models/maintenance/MaintenanceChecklist';
import { MaintenanceRequest } from '@/models/MaintenanceRequest';
import { sortItemsAsc } from "@/utils/sortItemsAsc";
import MaintenanceUploadMedia from "@/views/maintenance/maintenanceUploadMedia/MaintenanceUploadMedia.vue";

export default {
  name: 'MaintenanceRequestList',
  components: {
    MaintenanceQRCodeDlg,
    MaintenanceDueDateDialog,
    MassActionDialog,
    ValidationObserver,
    ValidationProvider,
    MaintenanceUploadMedia
  },
  mixins: [MaintenanceMixin, EventLocationsMixin],
  async created() {
    this.loading = true;
    await this.$store.dispatch('getMaintenanceRequests');
    await this.$store.dispatch('getMaintenanceTypes');
    this.isMaintenanceTechniciansLoading = true
    await this.$store.dispatch('getMaintenanceUsers');
    await this.$store.dispatch("getMaintenanceVendors");
    this.isMaintenanceTechniciansLoading = false
    await this.loadClosedRequests();
    await this.$store.dispatch('getAllClosedMaintenanceRequests');
    this.loading = false;

    if(this.$route.query?.tab) {
      this.tab = `tab-${this.$route.query?.tab}`;
    }

    // Initialize current page items after data is loaded
    this.$nextTick(() => {
      this.updateCurrentPageItems();
    });
  },
  data() {
    return {
      loading: true,
      uploadKey: 0,
      searchNew: '',
      searchAssigned: '',
      newMaintenanceRequest: new MaintenanceRequest(),
      newMaintRes: null,
      reqType: null,
      tab: null,
      dialog: false,
      startDate: moment().subtract(1, 'week').toISOString().substring(0, 10),
      endDate: moment().toISOString().substring(0, 10),
      maintenanceRequestsCompleted: [],
      isMaintenanceTechniciansLoading: true,
      selectedRequestsNew: [],
      selectedRequestsInProgress: [],
      selectedRequestsNewObjects: [],
      selectedRequestsInProgressObjects: [],
      currentPageItemsNew: [],
      currentPageItemsInProgress: [],
      currentNewPagination: { page: 1, itemsPerPage: 10 },
      currentInProgressPagination: { page: 1, itemsPerPage: 10 },
      sortByNew: 'dateTime',
      sortDescNew: true,
      sortByInProgress: 'dateTime',
      sortDescInProgress: true,
      isMediaUploading: false,
      picker: {
        start: false,
        end: false,
      },
      headers: [
        {
          text: '',
          align: '',
          sortable: false,
          width: 70,
          value: 'icon',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'GeneralTranslations.Resident'
          ],
          align: 'start',
          value: 'residentName',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'MaintenanceList.Request'
          ],
          align: 'start',
          sortable: false,
          value: 'friendlyText',
        },
        {
          text: 'Room / Location',
          align: 'start',
          value: 'roomName',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'GeneralTranslations.Category'
          ],
          value: 'category',
        },

        {
          text: this.$i18n.messages[this.$i18n.locale][
            'MaintenanceList.RequestAge'
          ],
          align: 'start',
          value: 'requestAge',
        },
        {
          text: 'Due Date',
          align: 'start',
          value: 'estimatedCompletionDate',
        },
      ],
      headersInProgress: [
        {
          text: '',
          align: '',
          sortable: false,
          width: 70,
          value: 'icon',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'GeneralTranslations.Resident'
          ],
          align: 'start',
          value: 'residentName',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'MaintenanceList.Request'
          ],
          align: 'start',
          sortable: false,
          value: 'friendlyText',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'GeneralTranslations.Category'
          ],
          value: 'category',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'GeneralTranslations.Status'
          ],
          value: 'status',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'MaintenanceList.RequestAge'
          ],
          align: 'start',
          value: 'requestAge',
        },
        {
          text: 'Priority',
          align: 'start',
          value: 'priority',
        },
        {
          text: 'Assignee',
          align: 'start',
          value: 'assignee',
        },
        {
          text: 'Due Date',
          align: 'start',
          value: 'estimatedCompletionDate',
        },
      ],
      headersClosed: [
        {
          text: '',
          align: '',
          sortable: false,
          width: 70,
          value: 'icon',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'GeneralTranslations.Resident'
          ],
          align: 'start',
          value: 'residentName',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'MaintenanceList.Request'
          ],
          align: 'start',
          sortable: false,
          value: 'friendlyText',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'GeneralTranslations.Category'
          ],
          value: 'category',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'GeneralTranslations.Status'
          ],
          value: 'status',
        },
        {
          text: 'Open Duration (in days)',
          align: 'start',
          value: 'openDuration',
        },
        {
          text: 'Priority',
          align: 'start',
          value: 'priority',
        },
        {
          text: 'Assignee',
          align: 'start',
          value: 'assignee',
        },
        {
          text: 'Due Date',
          align: 'start',
          value: 'estimatedCompletionDate',
        },
      ],
      jsonFields: {
        'Maintenance job': 'name',
        Resident: 'residentName',
        'Category': 'category',
        Status: {
          field: 'status',
          callback: value => {
            return this.getStatus(value);
          },
        },
        'Scheduled work order': 'customId',
        'Request date': {
          field: 'dateTime',
          callback: value => {
            const date = moment(value.seconds * 1000);
            return date.isValid() ? date.format('DD MMM YYYY, HH:mm A') : '';
          },
        },
        'Work order date': {
          field: 'assignedDate',
          callback: value => {
            const date = moment(value.seconds * 1000);
            return date.isValid() ? date.format('DD MMM YYYY, HH:mm A') : '';
          },
        },
        'Due Date': {
          field: 'estimatedCompletionDate',
          callback: value => {
            const date = moment(value, "YYYY-MM-DD");
            return date.isValid() ? date.format('DD MMM YYYY, HH:mm A') : '';
          },
        },
        'Completed date': {
          field: 'completedDateTime',
          callback: value => {
            const date = moment(value.seconds*1000);
            return date.isValid() ? date.format('DD MMM YYYY, HH:mm A') : '';
          },
        },
        'Days taken': 'openDuration',
        'Request age': 'hoursAgo',
        'Description': 'description',
        'Room Name': 'roomName',
        'Location': 'location',
        'Vendor Assigned':  {
          field: 'assignee',
          callback: value => {
            return value ? 'Yes' : 'No';
          },
        },
        'Vendor Name': 'assignee',
        'Vendor Contact email': 'assigneeEmail',
        'Vendor Contact Number': 'assigneePhoneNumber',
/*        'Risk factor': {
          field: 'risk',
          callback: value => {
            return value.charAt(0).toUpperCase() + value.slice(1);
          },
        },
        'Risk Text': 'riskDetails',*/
        'Priority': 'priority',
        'Type': {
          field: 'maintenanceType',
          callback: value => {
            return value.charAt(0).toUpperCase() + value.slice(1);
          },
        },
      },
      allRequestsJsonFields: {
        'Maintenance job': 'name',
        Resident: 'residentName',
        'Category': 'category',
        Status: {
          field: 'status',
          callback: value => {
            return this.getStatus(value);
          },
        },
        'Scheduled work order': 'customId',
        'Request date': {
          field: 'dateTime',
          callback: value => {
            const date = moment(value.seconds * 1000);
            return date.isValid() ? date.format('DD MMM YYYY, HH:mm A') : '';
          },
        },
        'Work order date': {
          field: 'assignedDate',
          callback: value => {
            const date = moment(value.seconds * 1000);
            return date.isValid() ? date.format('DD MMM YYYY, HH:mm A') : '';
          },
        },
        'Due Date': {
          field: 'estimatedCompletionDate',
          callback: value => {
            const date = moment(value, "YYYY-MM-DD");
            return date.isValid() ? date.format('DD MMM YYYY, HH:mm A') : '';
          },
        },
        'Completed date': {
          field: 'completedDateTime',
          callback: value => {
            const date = moment(value.seconds*1000);
            return date.isValid() ? date.format('DD MMM YYYY, HH:mm A') : '';
          },
        },
        'Days taken': 'openDuration',
        'Request age': 'hoursAgo',
        'Description': 'description',
        'Room Name': 'roomName',
        'Location': 'location',
        'Vendor Assigned':  {
          field: 'assignee',
          callback: value => {
            return value ? 'Yes' : 'No';
          },
        },
        'Vendor Name': 'assignee',
        'Vendor Contact email': 'assigneeEmail',
        'Vendor Contact Number': 'assigneePhoneNumber',
/*        'Risk factor': {
          field: 'risk',
          callback: value => {
            return value.charAt(0).toUpperCase() + value.slice(1);
          },
        },
        'Risk Text': 'riskDetails',*/
        'Priority': 'priority',
        'Type': {
          field: 'maintenanceType',
          callback: value => {
            return value.charAt(0).toUpperCase() + value.slice(1);
          },
        },
      }
    };
  },
  computed: {
    MaintenanceTypes() {
      return MaintenanceTypes
    },
    maintenanceTechnicians() {
      return sortItemsAsc([...this.$store.state.userModule.maintenanceUsers, ...this.$store.state.userModule.maintenanceVendors.map(x => { return {...x, isVendor: true} })], 'name')
    },
    maintenanceRequestsInProgress() {
      let maintenanceRequests =
        this.$store.state.maintenanceModule.maintenanceRequests.filter(
          function (el) {
            return el.status == 'inprogress';
          }
        );

      maintenanceRequests.forEach(m => {
        const isHour = m.requestAge >= 60;
        const value = isHour
          ? Number((m.requestAge / 60).toFixed(0))
          : m.requestAge.toFixed(0);
        m.hoursAgo = `${value} ${
          isHour
            ? this.$t(`GeneralTranslations.Hour${value > 1 ? 's' : ''}`)
            : this.$t(`GeneralTranslations.Minute${value > 1 ? 's' : ''}`)
        }`;
        m.requestAgeColor = this.getColor(m.requestAge);

        if (m.priority == 'Urgent' || m.priority == 'High') {
          m.priorityColor = 'red--text';
        } else if (m.priority == 'Medium') {
          m.priorityColor = 'orange--text';
        } else {
          m.priorityColor = 'green--text';
        }
      });

      maintenanceRequests = maintenanceRequests.sort(function (a, b) {
        // Turn your strings into dates, and then subtract them
        // to get a value that is either negative, positive, or zero.
        return (
          new Date(b.estimatedCompletionDate) -
          new Date(a.estimatedCompletionDate)
        );
      });

      return maintenanceRequests;
    },
    maintenanceTypes() {
      return this.$store.state.maintenanceModule.maintenanceTypes;
    },
    closedMaintenanceRequests() {
      return this.$store.state.maintenanceModule.closedMaintenanceRequests;
    },
    allClosedMaintenanceRequests() {
      return this.$store.state.maintenanceModule.allClosedMaintenanceRequests;
    },
    residentsList() {
      return this.$store.state.residentModule.residentsShortData.filter(resident => resident.status !== ResidentStatus.Departed);
    },
    closedByCount() {
      return this.$store.state.maintenanceModule.closedMaintenanceRequests.reduce(
        (acc, req) => {
          if (req?.completedBy) {
            acc[req.completedBy] = acc[req.completedBy] || 0;
            acc[req.completedBy] += 1;
          }
          return acc;
        },
        {}
      );
    },
    closedByPriority() {
      return this.$store.state.maintenanceModule.closedMaintenanceRequests.reduce(
        (acc, req) => {
          if (req?.priority) {
            acc[req.priority] = acc[req.priority] || 0;
            // The more elegant way: acc[languageId] ??= {name: languageId, count: 0};
            acc[req.priority] += 1;
          }
          return acc;
        },
        {}
      );
    },
    closedByCategory() {
      return this.$store.state.maintenanceModule.closedMaintenanceRequests.reduce(
        (acc, req) => {
          if (req?.category) {
            acc[req.category] = acc[req.category] || 0;
            // The more elegant way: acc[languageId] ??= {name: languageId, count: 0};
            acc[req.category] += 1;
          }
          return acc;
        },
        {}
      );
    },
    maintenanceRequestsNew() {
      const maintenanceRequests =
        this.$store.state.maintenanceModule.maintenanceRequests.filter(
          function (el) {
            return el.status == 'new';
          }
        );

      maintenanceRequests.forEach(m => {
        const isHour = m.requestAge >= 60;
        const value = isHour
          ? Number((m.requestAge / 60).toFixed(0))
          : m.requestAge.toFixed(0);
        m.hoursAgo = `${value} ${
          isHour
            ? this.$t(`GeneralTranslations.Hour${value > 1 ? 's' : ''}`)
            : this.$t(`GeneralTranslations.Minute${value > 1 ? 's' : ''}`)
        }`;
        m.requestAgeColor = this.getColor(m.requestAge);
      });

      return maintenanceRequests;
    },
    facility() {
      return this.$store.state.facilityModule.selectedFacility;
    },
    formattedStartDate() {
      return moment(this.startDate).format(this.facility.dateFormat);
    },
    formattedEndDate() {
      return moment(this.endDate).format(this.facility.dateFormat);
    },
    mergeAllRequests() {
      return [...this.maintenanceRequestsNew, ...this.maintenanceRequestsInProgress, ...this.allClosedMaintenanceRequests]
    },
    user() {
      return this.$store.state.authModule.user;
    },
    maintenancePriority() {
      return Object.values(MaintenancePriority);
    },
    currentUser(){
        return this.$store.state.authModule.user
    },
    canAddEventLocation(){
        return hasAccess(this.currentUser.accessLevel, AccessMatrixKeys.Calendar_Events_AddLocation)
    },
    canUploadMaintenenceMedia(){
        return hasAccess(this.currentUser.accessLevel, AccessMatrixKeys.Maintenance_Requests_UploadMedia)
    },
    isAllNewSelected() {
      return this.selectedRequestsNew.length > 0 && 
             this.selectedRequestsNew.length === this.maintenanceRequestsNew.length;
    },
    isAllInProgressSelected() {
      return this.selectedRequestsInProgress.length > 0 && 
             this.selectedRequestsInProgress.length === this.maintenanceRequestsInProgress.length;
    },
    isAllNewSelectedOnPage() {
      if (this.currentPageItemsNew.length === 0) return false;
      return this.currentPageItemsNew.every(item => this.selectedRequestsNew.includes(item.id));
    },
    isAllInProgressSelectedOnPage() {
      if (this.currentPageItemsInProgress.length === 0) return false;
      return this.currentPageItemsInProgress.every(item => this.selectedRequestsInProgress.includes(item.id));
    },
    canPerformMassActions() {
      const user = this.$store.state.authModule.user;
      return user.accessLevel === 'Maintenance Admin' || 
             user.accessLevel === 'Org Admin';
    }
  },
  watch: {
    newMaintRes(res) {
      if(res != null){
      this.newMaintenanceRequest.residentId = res.id;
      this.newMaintenanceRequest.residentName = res.name;
      this.newMaintenanceRequest.roomName = res.roomName;
    }
    },
    reqType(requestType) {
      if(requestType) {
        this.newMaintenanceRequest.name = requestType?.name;
        this.newMaintenanceRequest.category = requestType?.category;
        this.newMaintenanceRequest.categoryId = requestType?.categoryId;
        this.newMaintenanceRequest.department = requestType?.department;
        this.newMaintenanceRequest.typeId = requestType?.id;
        this.newMaintenanceRequest.icon = requestType?.icon;
      }
    },
    'newMaintenanceRequest.locationId'(locationId, oldlocationId) {
      if(oldlocationId !== locationId) {
        this.newMaintenanceRequest.location = null;
      }

      if(locationId != null) {
        this.newMaintenanceRequest.location = this.eventLocations?.find(x => x.id === locationId)?.name;
      }
    },
    'newMaintenanceRequest.wingId'(wingId, oldwingId) {
      if(oldwingId !== wingId) {
        this.newMaintenanceRequest.wing = null;
      }

      if(wingId != null) {
        this.newMaintenanceRequest.wing = this.wings?.find(x => x.id === wingId)?.name;
      }
    },
    maintenanceRequestsNew() {
      this.$nextTick(() => {
        this.updateCurrentPageItems();
      });
    },
    maintenanceRequestsInProgress() {
      this.$nextTick(() => {
        this.updateCurrentPageItems();
      });
    },
    selectedRequestsNewObjects(newVal) {
      this.selectedRequestsNew = newVal.map(obj => obj.id);
    },
    selectedRequestsInProgressObjects(newVal) {
      this.selectedRequestsInProgress = newVal.map(obj => obj.id);
    },
  },
  methods: {
    convertToTranslationKey(text) {
      return toTranslationKey(text);
    },
    clearRes() {
      this.newMaintenanceRequest.residentId = null;
      this.newMaintenanceRequest.residentName = null;
      this.newMaintenanceRequest.roomName = null;
      this.newMaintRes = null;
    },
    async loadClosedRequests() {
      await this.$store.dispatch('getClosedMaintenanceRequests', {
        startDate: moment(this.startDate).startOf('day').toDate(),
        endDate: moment(this.endDate).endOf('day').toDate(),
      });
    },
    handleClickMaintenanceRequest(row, event) {
      // Don't navigate if clicking on checkbox or interactive elements
      if (event?.target?.closest('.v-simple-checkbox') || 
          event?.target?.closest('.v-data-table__checkbox') ||
          event?.target?.closest('button') ||
          event?.target?.closest('input')) {
        return;
      }
      
      router.push('/maintenanceRequests/' + row.id.toString());
    },
    onStaffAssign(item){
      const maintenanceTechnician = this.maintenanceTechnicians.find(x => x.id === item.id);
      this.newMaintenanceRequest.assignee = maintenanceTechnician?.name ?? null;
      this.newMaintenanceRequest.assigneeId = maintenanceTechnician?.id ?? null;
      this.newMaintenanceRequest.assigneeEmail = maintenanceTechnician?.email ?? null;
      this.newMaintenanceRequest.assigneePhoneNumber = maintenanceTechnician?.phoneNumber ?? null;
    },
    async submit() {
      this.newMaintenanceRequest.facilityId = this.facility.id;
      this.newMaintenanceRequest.createdByStaffId = this.user.id;
      this.newMaintenanceRequest.createdByStaffName = this.user.name;
      await this.$store.dispatch('createMaintenanceRequest', this.newMaintenanceRequest)
      this.$toast.open({
        type: "success",
        message: "Maintenance request has been successfully created"
      });
      this.dialog = false;
    },
    onMaintenanceMediaBeforeUpload(data){
      this.isMediaUploading = true
    },
    onMaintenanceMediaUploaded(data){
      this.newMaintenanceRequest.id = data.id
      this.newMaintenanceRequest.media = data.media
      this.isMediaUploading = false
    },
    getColor(minutes) {
      if (minutes < 1400) {
        return 'green--text';
      }
      if (minutes < 3000) {
        return 'orange--text';
      }
      return 'red--text';
    },
    checkExistCategory(category) {
      const key = toTranslationKey(category);
      return maintenanceCategoriesKeys.includes(key);
    },
    openQRCode() {
      this.$refs.maintenanceQRCodeDlg.open();
    },
    close() {
      this.uploadKey += 1;
      this.newMaintenanceRequest = new MaintenanceRequest();
      this.newMaintRes = null;
      this.reqType = null;
      this.dialog = false;
      this.$refs.form && this.$refs.form.reset();
      this.$refs.createMaintenanceRequestForm && this.$refs.createMaintenanceRequestForm.resetValidation();
    },
    getStatus(status) {
      switch (status) {
        case MaintenanceRequestStatus.New: {
          return this.$t("MaintenanceList.New");
        }
        case MaintenanceRequestStatus.InProgress: {
          return this.$t("MaintenanceList.InProgress");
        }
        case MaintenanceRequestStatus.Cancelled: {
          return this.$t("MaintenanceList.Cancelled");
        }
        case MaintenanceRequestStatus.Complete: {
          return this.$t("MaintenanceList.Completed");
        }
      }
    },
    addItemForChecklist() {
      const label = `Item ${this.newMaintenanceRequest.checklist.length + 1}`;
      this.newMaintenanceRequest.checklist.push(new MaintenanceChecklist(label, false).toObject());
    },
    removeItemFromChecklist(index) {
      this.newMaintenanceRequest.checklist.splice(index, 1);
      this.newMaintenanceRequest.checklist.forEach((item, index) => {
        if (item.label.includes('Item')) {
          item.label = `Item ${index + 1}`;
        }
      });
    },
    formatDueDate(dueDate) {
      if (!dueDate) return '';
      return moment(dueDate).format(this.facility.dateFormat || 'DD/MM/YYYY');
    },
    formatEstimatedCompletionDate(date) {
      if (!date) return '';
      return moment(date).format(this.facility.dateFormat || 'DD/MM/YYYY');
    },
    async refreshMaintenanceRequests() {
      await this.$store.dispatch('getMaintenanceRequests');
    },
    updateCurrentPageItems() {
      // Update current page items for both tables using stored pagination state
      const newStart = (this.currentNewPagination.page - 1) * this.currentNewPagination.itemsPerPage;
      const newEnd = newStart + this.currentNewPagination.itemsPerPage;
      this.currentPageItemsNew = this.maintenanceRequestsNew.slice(newStart, newEnd);

      const inProgressStart = (this.currentInProgressPagination.page - 1) * this.currentInProgressPagination.itemsPerPage;
      const inProgressEnd = inProgressStart + this.currentInProgressPagination.itemsPerPage;
      this.currentPageItemsInProgress = this.maintenanceRequestsInProgress.slice(inProgressStart, inProgressEnd);
    },
    onNewRequestsPagination(pagination) {
      // Store pagination state and update current page items
      this.currentNewPagination = { ...pagination };
      const { page, itemsPerPage } = pagination;
      const start = (page - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      this.currentPageItemsNew = this.maintenanceRequestsNew.slice(start, end);
    },
    onInProgressRequestsPagination(pagination) {
      // Store pagination state and update current page items
      this.currentInProgressPagination = { ...pagination };
      const { page, itemsPerPage } = pagination;
      const start = (page - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      this.currentPageItemsInProgress = this.maintenanceRequestsInProgress.slice(start, end);
    },
    clearSelection() {
      this.selectedRequestsNew = [];
      this.selectedRequestsInProgress = [];
    },
    openCancelDialog() {
      const selectedIds = this.getSelectedRequests();
      if (selectedIds.length > 0) {
        this.$refs.cancelDialog.open(selectedIds);
      }
    },
    openChangeDueDateDialog() {
      const selectedIds = this.getSelectedRequests();
      if (selectedIds.length > 0) {
        this.$refs.dueDateDialog.open(selectedIds);
      }
    },
    openAssignToDialog() {
      const selectedIds = this.getSelectedRequests();
      if (selectedIds.length > 0) {
        this.$refs.assignDialog.open(selectedIds);
      }
    },
    openPriorityDialog() {
      const selectedIds = this.getSelectedRequests();
      if (selectedIds.length > 0) {
        this.$refs.priorityDialog.open(selectedIds);
      }
    },
    async onMassActionCompleted() {
      // Clear selections
      this.selectedRequestsNew = [];
      this.selectedRequestsInProgress = [];
      this.selectedRequestsNewObjects = [];
      this.selectedRequestsInProgressObjects = [];
      
      // Refresh all maintenance requests data
      await Promise.all([
        this.$store.dispatch('getMaintenanceRequests'),
        this.loadClosedRequests(),
        this.$store.dispatch('getAllClosedMaintenanceRequests')
      ]);
    },
    async onTabChange(tabValue) {
      // Refresh data when switching tabs
      if (tabValue === 'tab-1' || tabValue === 'tab-2') {
        await this.$store.dispatch('getMaintenanceRequests');
      } 
      else if (tabValue === 'tab-3') {
        await Promise.all([
          this.loadClosedRequests(),
          this.$store.dispatch('getAllClosedMaintenanceRequests')
        ]);
      }
    },
    getSelectedRequests() {
      if (this.tab === 'tab-1') {
        return this.selectedRequestsNew;
      } else if (this.tab === 'tab-2') {
        return this.selectedRequestsInProgress;
      }
      return [];
    },
  },
};
</script>

<style src="./maintenanceList.scss" lang="scss" scoped></style>